import { Component, DestroyRef, Input, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { merge, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { FormInlineErrorComponent, State } from '@k12/forms';
import { CountiesService, CountyResponse } from '@k12/school-services';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-business-address',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    NgxMaskDirective,
    FormInlineErrorComponent
  ],
  templateUrl: './business-address.component.html',
  providers: [provideNgxMask()]
})
export class BusinessAddressComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() states: State[] = [];
  @Input() showReviewStatus = true;

  private readonly countiesService = inject(CountiesService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly personalFieldNames = [
    'personalAddress1',
    'personalCity',
    'personalState',
    'personalZip',
    'personalCounty',
    'phone',
    'secondaryPhone'
  ] as const;

  counties: CountyResponse[] = [];
  isNorthCarolina = signal(false);
  filteredStates!: Observable<State[]>;
  filteredCounties!: Observable<CountyResponse[]>;
  
  public readonly cityErrorMessages: Record<string, string | ((value: unknown) => string)> = {
    required: 'This field is required',
    name: 'City must contain only letter and special characters'
  };

  ngOnInit(): void {
    this.filteredStates = this.formGroup.get('state')!.valueChanges.pipe(
      startWith(this.formGroup.get('state')!.value),
      map((value: string | State) => this._filterStates(value))
    );

    this.formGroup
      .get('state')!
      .valueChanges.pipe(startWith(this.formGroup.get('state')!.value))
      .subscribe((state: string | State | null | undefined) => {
        let stateCode: string | undefined;

        if (typeof state === 'object' && state?.code) {
          stateCode = state.code;
        } else if (typeof state === 'string') {
          const matched = this.states.find((s) => s.code === state || s.name === state);
          stateCode = matched?.code;
        }

        this.isNorthCarolina.set(stateCode === 'NC');

        if (this.isNorthCarolina()) {
          this.loadCounties('NC');
        }

        if (!this.isNorthCarolina()) {
          this.formGroup.patchValue({ county: '' }, { emitEvent: false });
        }
      });

    this.filteredCounties = this.formGroup.get('county')!.valueChanges.pipe(
      startWith(this.formGroup.get('county')!.value || ''),
      map((value: string) => this._filterCounties(value || ''))
    );

    if (!this.showReviewStatus) {
      this.setupBusinessSameAsPersonalSync();
    }
  }

  private setupBusinessSameAsPersonalSync(): void {
    const sameAsPersonalControl = this.formGroup.get('businessSameAsPersonal');
    if (!sameAsPersonalControl) return;

    sameAsPersonalControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((checked: boolean) => {
      if (checked) {
        this.copyPersonalToBusiness();
      }
    });

    const personalControls = this.personalFieldNames
      .map((name) => this.formGroup.get(name))
      .filter((control): control is NonNullable<typeof control> => !!control);

    if (personalControls.length) {
      merge(...personalControls.map((control) => control.valueChanges))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (sameAsPersonalControl.value) {
            this.copyPersonalToBusiness();
          }
        });
    }
  }

  private copyPersonalToBusiness(): void {
    const email = this.formGroup.get('email')?.value ?? '';

    this.formGroup.patchValue({
      address1: this.formGroup.get('personalAddress1')?.value ?? '',
      city: this.formGroup.get('personalCity')?.value ?? '',
      state: this.formGroup.get('personalState')?.value,
      zip: this.formGroup.get('personalZip')?.value ?? '',
      county: this.formGroup.get('personalCounty')?.value ?? '',
      businessPhone: this.formGroup.get('phone')?.value ?? '',
      businessSecondaryPhone: this.formGroup.get('secondaryPhone')?.value ?? ''
    });

    this.formGroup.get('businessEmail')?.setValue(email, { emitEvent: false });
  }

  private _filterStates(value: string | State): State[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value?.name?.toLowerCase() || '';
    return this.states.filter((state: State) => state.name.toLowerCase().includes(filterValue));
  }

  private _filterCounties(value: string): CountyResponse[] {
    const filterValue = value.toLowerCase();
    return this.counties.filter((county) => county.name.toLowerCase().includes(filterValue));
  }

  displayStateName(state: State | null): string {
    return state?.name || '';
  }

  private loadCounties(state: string): void {
    this.countiesService.getCountiesByState(state).subscribe({
      next: (data: CountyResponse[]) => {
        this.counties = data;
        this.formGroup.get('county')!.updateValueAndValidity({ emitEvent: true });
      },
      error: () => {
        this.counties = [];
      }
    });
  }
}
